import { useParams, Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import Header from '../components/Header'
import Footer from '../components/Footer'
import VoteWidget from '../components/VoteWidget'
import AuthorAvatar from '../components/AuthorAvatar'
import { getAuthor } from '../data/authors'
import { articles, featuredArticle, type Article } from '../data/articles'

const allArticles = [featuredArticle, ...articles]

const rawContent = import.meta.glob('../content/articles/*.html', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

function contentFor(slug: string): string | undefined {
  const entry = Object.entries(rawContent).find(([path]) => path.endsWith(`/${slug}.html`))
  return entry?.[1]
}

// Same author first, then same tag, then whatever's left — capped at 3.
// Simple heuristic, no real recommendation engine, but real internal links
// beat none: helps crawl depth and keeps readers on related dispatches.
function relatedTo(article: Article): Article[] {
  const others = allArticles.filter((a) => a.slug !== article.slug)
  const sameAuthor = others.filter((a) => a.authorSlug === article.authorSlug)
  const sameTag = others.filter((a) => a.tag === article.tag && a.authorSlug !== article.authorSlug)
  const rest = others.filter((a) => a.authorSlug !== article.authorSlug && a.tag !== article.tag)
  return [...sameAuthor, ...sameTag, ...rest].slice(0, 3)
}

export default function ArticleDetail() {
  const { slug } = useParams()
  const article = allArticles.find((a) => a.slug === slug)

  if (!article) {
    return (
      <div className="os-root">
        <Head>
          <title>Not Found · Original Savage</title>
        </Head>
        <Header />
        <main id="main">
          <section className="wrap sec">
            <h1>That dispatch doesn't exist (yet)</h1>
            <p>
              <Link to="/articles">Back to the full archive &rarr;</Link>
            </p>
          </section>
        </main>
        <Footer />
      </div>
    )
  }

  const author = getAuthor(article.authorSlug)
  const body = contentFor(article.slug) ?? '<p>Content coming soon.</p>'
  const pageUrl = `https://originalsavage.com/articles/${article.slug}`
  const related = relatedTo(article)
  // `dek` is the on-page standfirst and runs long; `metaDescription` is the SERP-length one.
  const metaDesc = article.metaDescription ?? article.dek

  return (
    <div className="os-root">
      <Head>
        <title>{article.title} · Original Savage</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content="https://originalsavage.com/brand/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content="https://originalsavage.com/brand/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Article',
                '@id': `${pageUrl}#article`,
                headline: article.title,
                description: metaDesc,
                url: pageUrl,
                author: {
                  '@type': 'Person',
                  name: author.name,
                  url: `https://originalsavage.com/authors/${author.slug}`,
                },
                publisher: { '@id': 'https://originalsavage.com/#organization' },
                image: 'https://originalsavage.com/brand/og-image.png',
              },
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://originalsavage.com/' },
                  { '@type': 'ListItem', position: 2, name: 'Dispatches', item: 'https://originalsavage.com/articles' },
                  { '@type': 'ListItem', position: 3, name: article.title, item: pageUrl },
                ],
              },
            ],
          })}
        </script>
      </Head>
      <Header />
      <main id="main">
        <article className="wrap sec art-detail">
          <div className="art-detail-meta">
            <span className="tag">{article.tag}</span>
            <span className="stamp tnum">{article.date}</span>
          </div>
          <h1>{article.title}</h1>
          <p className="dek art-detail-dek">{article.dek}</p>
          <div className="art-detail-byline">
            <Link to={`/authors/${author.slug}`}>
              <AuthorAvatar author={author} size={40} />
            </Link>
            <div>
              <Link className="by" to={`/authors/${author.slug}`}>
                {author.name}
              </Link>
              <div className="author-role">{author.role}</div>
            </div>
          </div>

          {article.vote && (
            <div className="art-detail-vote">
              <VoteWidget poll={article.vote} />
            </div>
          )}

          <div className="article-body" dangerouslySetInnerHTML={{ __html: body }} />

          {related.length > 0 && (
            <div className="art-detail-related">
              <h2>Related Dispatches</h2>
              <ul>
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link to={`/articles/${r.slug}`}>{r.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="art-detail-back">
            <Link to="/articles">&larr; Back to the full archive</Link>
          </p>
        </article>
      </main>
      <Footer />
    </div>
  )
}
